import { Children, isValidElement, useMemo, useRef } from 'react';
import type { ComponentPropsWithoutRef, ElementType, PropsWithChildren } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { toLinearGradient } from '#src/app/shared/utils/color';

import { useScrollContainer } from './hooks/useScrollContainer';
import { useScrollEnd } from './hooks/useScrollEnd';
import { NavigationButtons } from './NavigationButtons';

type Props = PropsWithChildren<
	Omit<ComponentPropsWithoutRef<'section'>, 'color'> & {
		label?: string;
		as?: ElementType;
		backgroundColor?: string;
		ref?: React.Ref<HTMLElement>;
		/** Strips the list semantics (role="none" on the ul/li) so screen readers don't announce "list start" / "list, N items" boundaries; use when the first card names the carousel itself. */
		presentational?: boolean;
	}
>;

const OUTER_GRID_STYLE: React.CSSProperties = {
	gridTemplateColumns:
		'[carousel-gutter] calc(36px + (8px * 2)) [carousel-scroller] 1fr [carousel-gutter] calc(36px + (8px * 2))',
	gridTemplateRows: '[carousel-scroller] 1fr',
};

const LIST_CLASS =
	'col-[1/-1] row-[1] grid grid-flow-col gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory pb-2.5 motion-safe:scroll-smooth md:pb-0 [scrollbar-color:transparent_transparent] [scrollbar-width:thin] hover:[scrollbar-color:gray_transparent] md:[scrollbar-width:none] [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-0 md:[&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb]:bg-[gray]';

export const ScrollContainer: React.FC<Props> = ({
	children,
	label,
	backgroundColor = 'transparent',
	as: As = 'section',
	className,
	style,
	ref,
	presentational,
	...rest
}) => {
	const carouselRef = useRef<HTMLElement | null>(null);
	const { data, actions } = useScrollContainer(carouselRef);

	useScrollEnd(carouselRef, actions.handleScrollEnd);

	const totalItems = useMemo(() => Children.count(children), [children]);

	if (totalItems === 0) return null;

	return (
		<As
			aria-label={label}
			ref={ref}
			className={cn('relative grid overflow-hidden', className)}
			style={{ ...OUTER_GRID_STYLE, ...style }}
			{...rest}
		>
			<ul
				id="scroll-container-list"
				role={presentational ? 'none' : 'list'}
				ref={carouselRef as React.Ref<HTMLUListElement>}
				className={LIST_CLASS}
			>
				{Children.map(children, (child, index) => {
					if (!isValidElement(child)) return null;

					return (
						<li
							id={`scroll-container-list-item-${index}`}
							role={presentational ? 'none' : 'listitem'}
							// eslint-disable-next-line react/no-array-index-key
							key={`${label}-${index}-${totalItems}`}
							className="snap-start [scroll-snap-stop:always]"
						>
							{child}
						</li>
					);
				})}
			</ul>
			{/* Must stay after the list in the DOM so screen readers announce the cards before the nav controls; grid placement keeps the buttons visually at the edges. */}
			<NavigationButtons
				previous={{ onClick: actions.scrollToLeft, disabled: data.scrollLeftDisabled }}
				next={{ onClick: actions.scrollToRight, disabled: data.scrollRightDisabled }}
				prevFade={toLinearGradient(backgroundColor, 'left')}
				nextFade={toLinearGradient(backgroundColor, 'right')}
			/>
		</As>
	);
};
